import { type CustomCellEditorProps } from "ag-grid-react";
import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import { type ReactNode, useEffect, useRef } from "react";

import { BTCheckbox } from "@btnet/core-components";

import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

import {
    type CheckBoxCustomFieldDefinition,
    type CheckBoxCustomFieldValue,
    type CustomFieldAssociatedType,
} from "handlers";

const CheckboxCellEditor = (props: CustomCellEditorProps<unknown, boolean>) => {
    const valueRef = useRef(props.value);
    const onValueChangeRef = useRef(props.onValueChange);
    const stopEditingRef = useRef(props.stopEditing);

    useEffect(() => {
        onValueChangeRef.current(!valueRef.current);
        stopEditingRef.current();
    }, []);

    return null;
};

export const CheckBoxCustomFieldHandler: ICustomFieldTypeHandler<
    boolean,
    CheckBoxCustomFieldValue,
    CheckBoxCustomFieldDefinition
> = {
    getValue(field): boolean | null {
        return field.value ?? false;
    },

    setValue(field, value): CheckBoxCustomFieldValue {
        return { ...field, value: value ?? false };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: boolean | null,
    ): CheckBoxCustomFieldValue {
        return {
            dataType: "CheckBox",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? false,
        };
    },

    compare(valueA: boolean | null, valueB: boolean | null): number {
        const a = valueA ?? false;
        const b = valueB ?? false;
        if (a === b) return 0;
        return a ? 1 : -1;
    },

    renderDisplay(value: boolean | null): ReactNode {
        return (
            <div
                style={{
                    pointerEvents: "none",
                    marginLeft: "8px",
                }}
            >
                <BTCheckbox
                    id="checkboxCustomFieldDisplay"
                    data-testid="checkboxCustomFieldDisplay"
                    checked={value ?? false}
                />
            </div>
        );
    },

    renderEditor(
        value: boolean | null,
        customFieldId: number,
        onChange: (newValue: boolean) => void,
        _onBlur: () => void,
    ): ReactNode {
        return (
            <BTCheckbox
                id={`checkboxCustomFieldEditor-${customFieldId}`}
                data-testid="checkboxCustomFieldEditor"
                checked={value ?? false}
                onChange={(e: CheckboxChangeEvent) => onChange(e.target.checked)}
                style={{ marginLeft: "8px" }}
            />
        );
    },

    getAgGridCellEditor() {
        return CheckboxCellEditor;
    },

    renderAgGridCell(context): ReactNode {
        return defaultRenderAgGridCell(context);
    },
};
